import { ReactNode } from 'react';

import styled from '@emotion/styled';
import { Box, Button, getCSSVariable, Text } from '@opendoor/bricks-next';

import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';

import { useHeroContext } from './HeroShared';
import { TrustPilotWithUserImages } from './TrustPilotWithUserImages';

export interface HeroTitleAndAddressProps {
  analyticsId?: string;
  title: string;
  subtitle?: string;
  showAddressInput?: boolean;
  renderDesktopInput?: () => ReactNode;
  renderMobileInput?: () => ReactNode;
  additionalAddressContent?: ReactNode;
  addressEntryRef?: (node?: Element | null) => void;
  // Default input props (used when renderDesktopInput/renderMobileInput are not provided)
  addressCtaText?: string;
  addressCtaTextSmall?: string;
  addressCtaPlaceholderText?: string;
  addressCtaPlaceholderTextSmall?: string;
}

// Internal component: HeroHeadline
interface HeroHeadlineProps {
  title: string;
  subtitle?: string;
}

const HeroHeadline = ({ title, subtitle }: HeroHeadlineProps) => {
  return (
    <Box
      flexDirection="column"
      gap="$12x"
      alignItems="center"
      $largerThanMD={{ gap: '$8x', alignItems: 'flex-start' }}
    >
      <Text
        tag="h1"
        typography="$displayXsmall"
        textAlign="center"
        $largerThanSM={{ typography: '$displaySmall' }}
        $largerThanMD={{ typography: '$displayMedium', textAlign: 'left' }}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          tag="p"
          typography="$bodyMedium"
          textAlign="center"
          $largerThanSM={{ typography: '$bodyLarge' }}
          $largerThanMD={{ textAlign: 'left' }}
        >
          {subtitle}
        </Text>
      )}
    </Box>
  );
};

// Internal component: HeroAddressSection
interface HeroAddressSectionProps {
  showAddressInput?: boolean;
  renderDesktopInput: () => ReactNode;
  renderMobileInput: () => ReactNode;
  additionalContent?: ReactNode;
  isTreatment: boolean;
}

const StyledBox = styled(Box)`
  div > div {
    border-color: ${getCSSVariable('$borderAccentBrand')} !important;
  }
`;
const HeroAddressSection = ({
  showAddressInput,
  renderDesktopInput,
  renderMobileInput,
  isTreatment,
}: HeroAddressSectionProps) => {
  if (!showAddressInput) return null;

  const UseBox = isTreatment ? StyledBox : Box;

  return (
    <Box zIndex={10} className="hp-reskin-v2-address-entry-wrapper">
      <UseBox id="desktop-address-search" display="none" $largerThanLG={{ display: 'block' }}>
        {renderDesktopInput()}
      </UseBox>
      <UseBox id="mobile-address-search" display="block" $largerThanLG={{ display: 'none' }}>
        {renderMobileInput()}
      </UseBox>
      {isTreatment && (
        <Text
          typography="$bodyMedium"
          tag="p"
          color="$contentSecondary"
          textAlign="center"
          marginTop="$6x"
          $largerThanLG={{
            textAlign: 'left',
          }}
        >
          No obligation. Takes 5 minutes. Your information stays private.
        </Text>
      )}
    </Box>
  );
};

const addressInputCommonProps = (analyticsId?: string) => ({
  hideLabel: true,
  inputLocation: 'hero-cta' as const,
  analyticsPrefix: `cosmos-landing-page-hero-v2${analyticsId ? `-${analyticsId}` : ''}`,
  trackingTaxonomy: 'new_homepage' as const,
});

const StyledTreatmentButton = styled(Button)`
  height: 60px !important;
  margin-top: -17.5px;
  margin-right: -15px;
`;

const TreatmentButton = ({
  label,
  analyticsPrefix,
  onPress,
  isLoading,
}: {
  label: string;
  analyticsPrefix: string;
  onPress?: () => void;
  isLoading?: boolean;
}) => (
  <StyledTreatmentButton
    disabled={isLoading}
    analyticsName={analyticsPrefix}
    label={isLoading ? 'Loading' : label}
    size="medium"
    variant="primary"
    onPress={onPress}
  />
);

export const HeroTitleAndAddress = ({
  analyticsId,
  title,
  subtitle,
  showAddressInput,
  renderDesktopInput,
  renderMobileInput,
  additionalAddressContent,
  addressEntryRef,
  addressCtaText = 'Get cash offer',
  addressCtaTextSmall = 'Get offer',
  addressCtaPlaceholderText,
  addressCtaPlaceholderTextSmall,
}: HeroTitleAndAddressProps) => {
  const { analyticsPrefix } = useHeroContext();

  const isTreatment = analyticsId === 'big-swing-hero-v2';

  const treatmentCommonProps = addressInputCommonProps(analyticsId);
  const defaultRenderDesktopInput = () => (
    <NewSellerFlowAddressInput
      {...treatmentCommonProps}
      placeholder={addressCtaPlaceholderText || 'Enter your home address'}
      actionButton={(onPress, isLoading) => (
        <TreatmentButton
          analyticsPrefix={analyticsPrefix}
          label={addressCtaText}
          onPress={onPress}
          isLoading={isLoading}
        />
      )}
    />
  );

  const defaultRenderMobileInput = () => (
    <NewSellerFlowAddressInput
      {...treatmentCommonProps}
      placeholder={addressCtaPlaceholderTextSmall || 'Enter an address'}
      actionButton={(onPress, isLoading) => (
        <TreatmentButton
          analyticsPrefix={analyticsPrefix}
          label={addressCtaTextSmall}
          onPress={onPress}
          isLoading={isLoading}
        />
      )}
    />
  );

  return (
    <Box
      id="title-and-address-entry"
      flexDirection="column"
      width="100%"
      gap={isTreatment ? '$8x' : '$16x'}
      $largerThanSM={{ gap: isTreatment ? '$20x' : '$12x' }}
      $largerThanLG={{ gap: isTreatment ? '$24x' : '$12x' }}
    >
      <HeroHeadline title={title} subtitle={subtitle} />
      <Box display="none" ref={addressEntryRef as never} />
      <HeroAddressSection
        showAddressInput={showAddressInput}
        renderDesktopInput={renderDesktopInput || defaultRenderDesktopInput}
        renderMobileInput={renderMobileInput || defaultRenderMobileInput}
        additionalContent={additionalAddressContent}
        isTreatment={isTreatment}
      />
      {isTreatment && <TrustPilotWithUserImages />}
    </Box>
  );
};
